import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'http://localhost:5000';

const JourneyReview = ({ configId, journeyId, token, onConfirm }) => {
  const [config, setConfig] = useState(null);
  const [journey, setJourney] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingSession, setConfirmingSession] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAllData();
  }, [configId, journeyId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch config details
      const configRes = await axios.get(
        `${API_URL}/api/journey-configs/${configId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(configRes.data.config);

      // Fetch journey details (includes intake & framing)
      const journeyRes = await axios.get(
        `${API_URL}/api/journeys/${journeyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJourney(journeyRes.data.journey);

      // Fetch ideas
      const ideasRes = await axios.get(
        `${API_URL}/api/journeys/${journeyId}/ideas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIdeas(ideasRes.data.ideas || []);

      // Fetch existing confirmation (if any)
      try {
        const confirmRes = await axios.get(
          `${API_URL}/api/journeys/${journeyId}/confirmation`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setConfirmation(confirmRes.data.confirmation);
      } catch (err) {
        // No confirmation yet, that's okay
      }
    } catch (err) {
      console.error('Error fetching journey data:', err);
      setError(err.response?.data?.message || 'Failed to load journey data');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmJourney = async () => {
    try {
      setConfirmingSession(true);
      setError('');

      // Calculate total score
      const totalScore = ideas.length > 0
        ? (ideas.reduce((sum, idea) => sum + (idea.overall_score || 0), 0) / ideas.length).toFixed(1)
        : 0;

      // Save confirmation to database
      const res = await axios.post(
        `${API_URL}/api/journeys/${journeyId}/confirm`,
        {
          config_id: configId,
          ideas_count: ideas.length,
          total_score: parseFloat(totalScore),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setConfirmation(res.data.confirmation);
    } catch (err) {
      console.error('Error confirming journey:', err);
      setError(err.response?.data?.message || 'Failed to confirm journey');
    } finally {
      setConfirmingSession(false);
    }
  };

  if (loading) {
    return (
      <div className="form-card">
        <h2>Loading Journey Summary...</h2>
        <p>Please wait while we retrieve your journey data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-card">
        <h2>Error Loading Data</h2>
        <div className="form-error">{error}</div>
        <button className="btn btn-primary" onClick={fetchAllData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="form-card">
        <h2>✓ Journey Complete!</h2>
        <p style={{ color: '#666', fontSize: '16px', marginBottom: '20px' }}>
          Here's a summary of all the data you entered and submitted:
        </p>
      </div>

      {/* Journey Config Summary */}
      {config && (
        <div className="form-card">
          <h3>Journey Configuration</h3>
          <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '4px' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Title:</strong> {config.title}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Description:</strong> {config.description || '(No description)'}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Facilitator:</strong> {config.facilitator_name || 'Admin'}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Team Members:</strong> {config.team_member_count || 1}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Max Participants:</strong> {config.max_participants}
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Duration:</strong> {config.duration_days} days
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <span
                style={{
                  backgroundColor: config.status === 'active' ? '#e8f5e9' : '#fff3cd',
                  color: config.status === 'active' ? '#2e7d32' : '#856404',
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '12px',
                }}
              >
                {config.status}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Journey Details (Intake & Framing) */}
      {journey && (
        <div className="form-card">
          <h3>Journey Details</h3>
          <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '4px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginTop: 0 }}>Problem Statement (Intake)</h4>
              <div style={{ marginBottom: '12px' }}>
                <strong>Problem:</strong>
                <p style={{ marginTop: '5px', color: '#333' }}>
                  {journey.trigger_type || '(Not provided)'}
                </p>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Business Impact:</strong>
                <p style={{ marginTop: '5px', color: '#333' }}>
                  {journey.trigger_description || '(Not provided)'}
                </p>
              </div>
              <div>
                <strong>Scope:</strong>
                <p style={{ marginTop: '5px', color: '#333' }}>
                  {journey.applies_to || '(Not provided)'}
                </p>
              </div>
            </div>

            <div>
              <h4 style={{ marginTop: 0 }}>Problem Framing</h4>
              <p style={{ color: '#999', fontSize: '14px' }}>
                Framing data stored in database (linked via journey ID)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ideas Summary */}
      <div className="form-card">
        <h3>Ideas Submitted ({ideas.length})</h3>
        {ideas.length === 0 ? (
          <p style={{ color: '#999' }}>No ideas submitted</p>
        ) : (
          <div>
            {ideas.map((idea) => (
              <div
                key={idea.id}
                style={{
                  padding: '15px',
                  marginBottom: '15px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ marginBottom: '10px' }}>
                  <h4 style={{ margin: '0 0 5px 0' }}>{idea.title}</h4>
                  <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                    {idea.description}
                  </p>
                </div>

                {idea.overall_score ? (
                  <div
                    style={{
                      backgroundColor: '#e3f2fd',
                      padding: '10px',
                      borderRadius: '3px',
                      fontSize: '13px',
                    }}
                  >
                    <strong>Overall Score: {idea.overall_score}/10</strong>
                    <div style={{ marginTop: '5px', color: '#1976d2' }}>
                      • Impact: {idea.impact_score}/10
                      <br />
                      • Feasibility: {idea.feasibility_score}/10
                      <br />• Novelty: {idea.novelty_score}/10
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#999', fontSize: '13px' }}>Not yet scored</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Database Verification */}
      <div
        className="form-card"
        style={{
          backgroundColor: '#e8f5e9',
          borderLeft: '4px solid #4caf50',
        }}
      >
        <h3 style={{ color: '#2e7d32', margin: '0 0 10px 0' }}>✓ Data Verified in Database</h3>
        <p style={{ color: '#333', marginBottom: '10px' }}>
          All journey information has been successfully stored and retrieved from the database:
        </p>
        <ul style={{ marginLeft: '20px', color: '#333' }}>
          <li>
            Journey Configuration:
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '8px',
              borderRadius: '3px',
              fontFamily: 'monospace',
              fontSize: '12px',
              marginTop: '5px',
              wordBreak: 'break-all',
              cursor: 'pointer'
            }} title="Click to copy">
              {configId}
            </div>
          </li>
          <li style={{ marginTop: '10px' }}>
            Journey Instance:
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '8px',
              borderRadius: '3px',
              fontFamily: 'monospace',
              fontSize: '12px',
              marginTop: '5px',
              wordBreak: 'break-all',
              cursor: 'pointer'
            }} title="Click to copy">
              {journeyId}
            </div>
          </li>
          <li style={{ marginTop: '10px' }}>Ideas Recorded: {ideas.length} total</li>
          <li>All form data persisted and retrievable</li>
        </ul>
      </div>

      {/* Session Confirmation */}
      {confirmation ? (
        <div
          className="form-card"
          style={{
            backgroundColor: '#f3e5f5',
            borderLeft: '4px solid #9c27b0',
          }}
        >
          <h3 style={{ color: '#6a1b9a', margin: '0 0 15px 0' }}>✓ Journey Confirmed</h3>
          <div style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '4px', marginBottom: '15px' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Confirmation Session ID:</strong>
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '8px',
                borderRadius: '3px',
                fontFamily: 'monospace',
                fontSize: '12px',
                marginTop: '5px',
                wordBreak: 'break-all'
              }}>
                {confirmation.id}
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Confirmed At:</strong>
              <p style={{ marginTop: '5px', marginBottom: 0, color: '#333' }}>
                {new Date(confirmation.confirmed_at).toLocaleString()}
              </p>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Ideas Confirmed:</strong>
              <p style={{ marginTop: '5px', marginBottom: 0, color: '#333' }}>
                {confirmation.ideas_count} ideas
              </p>
            </div>
            {confirmation.total_score && (
              <div>
                <strong>Average Score:</strong>
                <p style={{ marginTop: '5px', marginBottom: 0, color: '#333' }}>
                  {confirmation.total_score}/10
                </p>
              </div>
            )}
          </div>
          <p style={{ color: '#666', fontSize: '13px', marginTop: '10px', marginBottom: 0 }}>
            ✓ This session has been permanently recorded in the database and can be retrieved later.
          </p>
        </div>
      ) : (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={handleConfirmJourney}
            disabled={confirmingSession}
            style={{ width: '100%' }}
          >
            {confirmingSession ? '⏳ Creating Confirmation Session...' : '✓ Create Confirmation Session'}
          </button>
          {error && (
            <div className="form-error" style={{ marginTop: '10px' }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Final Action Button */}
      {confirmation && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button className="btn btn-primary" onClick={onConfirm} style={{ width: '100%' }}>
            ✓ Return to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default JourneyReview;
